import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BIKE_TYPES = [
  "landevei", "gravel", "MTB", "FS", "Hybrid",
  "barnesykkel", "E-MTB", "E-FS", "E-trekking",
];

const TYPE_LABELS = {
  landevei: "Landevei", gravel: "Gravel", MTB: "MTB", FS: "FS",
  Hybrid: "Hybrid", barnesykkel: "Barnesykkel",
  "E-MTB": "E-MTB", "E-FS": "E-FS", "E-trekking": "E-trekking",
};

const STATUS_LABELS = {
  mottatt: "Mottatt", under_arbeid: "Under arbeid",
  "under arbeid": "Under arbeid", venter: "Venter",
  ferdig: "Ferdig", avsluttet: "Avsluttet", kansellert: "Kansellert",
};

const STATUS_COLORS = {
  mottatt: "bg-blue-100 text-blue-800",
  under_arbeid: "bg-yellow-100 text-yellow-800",
  "under arbeid": "bg-yellow-100 text-yellow-800",
  venter: "bg-orange-100 text-orange-800",
  ferdig: "bg-green-100 text-green-800",
  avsluttet: "bg-gray-100 text-gray-700",
  kansellert: "bg-red-100 text-red-700",
};

const fmtDate = (d) => {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "";
};

function serviceTotal(svc) {
  const svcSum = Array.isArray(svc.serviceLines)
    ? svc.serviceLines.reduce((s, l) => (l.reklamasjon || l.gratisarbeid ? s : s + (Number(l.pris) || 0)), 0) : 0;
  const prtSum = Array.isArray(svc.partsLines)
    ? svc.partsLines.reduce((s, l) => {
        if (l.isWarranty) return s;
        const unit = Number(l.price ?? l.pris ?? 0);
        const qty = Number(l.qty ?? l.antall ?? 1);
        return s + unit * qty - Number(l.discount ?? 0);
      }, 0) : 0;
  return svcSum + prtSum;
}

function serviceDesc(svc) {
  const lines = Array.isArray(svc.serviceLines)
    ? svc.serviceLines.map((l) => l.navn).filter(Boolean) : [];
  return lines.length ? lines.join(", ") : svc.arbeidsbeskrivelse || "";
}

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300";
const labelCls = "block text-xs font-medium text-gray-500 mb-1";
const btnSecondary = "text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 text-gray-700";
const btnDanger = "text-sm px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50";

function UploadZone({ label, accept, multiple, onFiles, uploading }) {
  const ref = useRef(null);
  return (
    <div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className={btnSecondary + " disabled:opacity-50"}
      >
        {uploading ? "Laster opp…" : label}
      </button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) { onFiles(e.target.files); e.target.value = ""; }
        }}
      />
    </div>
  );
}

function ReportCard({ title, hint, reports, uploading, onUpload, onDelete }) {
  const ref = useRef(null);
  return (
    <div className="border rounded-lg p-3 bg-white">
      <h2 className="font-semibold text-gray-800 mb-2 text-sm">{title}</h2>
      {reports?.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {reports.map((rep) => (
            <div key={rep.publicId} className="flex items-center gap-2 border rounded px-2 py-1.5 text-xs">
              <span className="text-lg leading-none">📄</span>
              <div className="flex-1 min-w-0">
                <a href={rep.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate block" title={rep.originalName}>
                  {rep.originalName || "Rapport"}
                </a>
                <span className="text-gray-400">{fmtDate(rep.uploadedAt)}</span>
              </div>
              <button onClick={() => onDelete(rep.publicId)} className="text-red-600 hover:text-red-800 shrink-0" title="Slett">×</button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className={btnSecondary + " w-full disabled:opacity-50"}
      >
        {uploading ? "Laster opp…" : "Last opp PDF"}
      </button>
      <input
        ref={ref}
        type="file"
        accept="application/pdf"
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files?.length) { onUpload(e.target.files); e.target.value = ""; } }}
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function LightboxModal({ src, alt, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-full rounded shadow-xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export default function BikeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [bike, setBike] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redigeringsskjema
  const [form, setForm] = useState({ merke: "", modell: "", type: "", elsykkel: null, årsmodell: "", rammenummer: "" });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // Bildeopplasting
  const [specUploading, setSpecUploading] = useState(false);
  const [mainUploading, setMainUploading] = useState(false);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [extraUploading, setExtraUploading] = useState(false);
  const [diagnoseUploading, setDiagnoseUploading] = useState(false);
  const [softwareUploading, setSoftwareUploading] = useState(false);
  const [imgMsg, setImgMsg] = useState(null);

  // Lightbox
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE_URL}/bikes/${id}/admin`)
      .then((r) => { if (!r.ok) throw new Error("Fant ikke sykkelkort"); return r.json(); })
      .then(({ bike: b, history: h }) => {
        setBike(b);
        setCustomer(b.customer || null);
        setHistory(h || []);
        setForm({
          merke: b.merke || "", modell: b.modell || "", type: b.type || "",
          elsykkel: b.elsykkel ?? null,
          årsmodell: b.årsmodell ? String(b.årsmodell) : "", rammenummer: b.rammenummer || "",
        });
        setLoading(false);
      })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [id]);

  const handleChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.merke.trim()) { setSaveMsg({ ok: false, text: "Merke er påkrevd" }); return; }
    if (!form.modell.trim()) { setSaveMsg({ ok: false, text: "Modell er påkrevd" }); return; }
    if (!form.type) { setSaveMsg({ ok: false, text: "Type er påkrevd" }); return; }
    if (form.elsykkel === null) { setSaveMsg({ ok: false, text: "Elsykkel / ikke elsykkel er påkrevd" }); return; }
    setSaving(true); setSaveMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${bike._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          merke: form.merke, modell: form.modell, type: form.type,
          elsykkel: form.elsykkel,
          årsmodell: form.årsmodell ? Number(form.årsmodell) : null,
          rammenummer: form.rammenummer,
        }),
      });
      if (!r.ok) throw new Error("Lagring feilet");
      const updated = await r.json();
      setBike((p) => ({ ...p, ...updated }));
      setSaveMsg({ ok: true, text: "Lagret" });
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  // -- Bildehjelper --
  const flashImgMsg = (ok, text) => {
    setImgMsg({ ok, text });
    setTimeout(() => setImgMsg(null), 3000);
  };

  const uploadSingle = async (endpoint, fieldName, file, setUploading, onSuccess) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append(fieldName, file);
      const r = await fetch(`${API_BASE_URL}${endpoint}`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      onSuccess(data);
      flashImgMsg(true, "Lastet opp");
    } catch (e) {
      flashImgMsg(false, e.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteSingle = async (endpoint, onSuccess) => {
    try {
      const r = await fetch(`${API_BASE_URL}${endpoint}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      onSuccess();
      flashImgMsg(true, "Slettet");
    } catch (e) {
      flashImgMsg(false, e.message);
    }
  };

  const handleSpecUpload = (files) => {
    uploadSingle(`/bikes/${bike._id}/spec-image`, "image", files[0],
      setSpecUploading,
      (data) => setBike((p) => ({ ...p, specImageUrl: data.specImageUrl }))
    );
  };

  const handleSpecDelete = () => {
    deleteSingle(`/bikes/${bike._id}/spec-image`,
      () => setBike((p) => ({ ...p, specImageUrl: "", specImagePublicId: "" }))
    );
  };

  const handleMainUpload = (files) => {
    uploadSingle(`/bikes/${bike._id}/main-image`, "image", files[0],
      setMainUploading,
      (data) => setBike((p) => ({ ...p, mainImageUrl: data.mainImageUrl }))
    );
  };

  const handleMainDelete = () => {
    deleteSingle(`/bikes/${bike._id}/main-image`,
      () => setBike((p) => ({ ...p, mainImageUrl: "", mainImagePublicId: "" }))
    );
  };

  const handleReceiptUpload = (files) => {
    uploadSingle(`/bikes/${bike._id}/receipt`, "file", files[0],
      setReceiptUploading,
      (data) => setBike((p) => ({ ...p, receiptUrl: data.receiptUrl }))
    );
  };

  const handleReceiptDelete = () => {
    deleteSingle(`/bikes/${bike._id}/receipt`,
      () => setBike((p) => ({ ...p, receiptUrl: "", receiptPublicId: "" }))
    );
  };

  const handleExtraUpload = async (files) => {
    setExtraUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("images", f));
      const r = await fetch(`${API_BASE_URL}/bikes/${bike._id}/extra-images`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setBike((p) => ({ ...p, extraImages: data.extraImages }));
      flashImgMsg(true, "Lastet opp");
    } catch (e) {
      flashImgMsg(false, e.message);
    } finally {
      setExtraUploading(false);
    }
  };

  const handleExtraDelete = async (publicId) => {
    try {
      const r = await fetch(
        `${API_BASE_URL}/bikes/${bike._id}/extra-images/${encodeURIComponent(publicId)}`,
        { method: "DELETE" }
      );
      if (!r.ok) throw new Error("Sletting feilet");
      const data = await r.json();
      setBike((p) => ({ ...p, extraImages: data.extraImages }));
      flashImgMsg(true, "Slettet");
    } catch (e) {
      flashImgMsg(false, e.message);
    }
  };

  const uploadReports = async (endpoint, field, files, setUploading) => {
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const r = await fetch(`${API_BASE_URL}${endpoint}`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setBike((p) => ({ ...p, [field]: data[field] }));
      flashImgMsg(true, "Lastet opp");
    } catch (e) {
      flashImgMsg(false, e.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteReport = async (endpoint, field, publicId) => {
    try {
      const r = await fetch(`${API_BASE_URL}${endpoint}/${encodeURIComponent(publicId)}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      const data = await r.json();
      setBike((p) => ({ ...p, [field]: data[field] }));
      flashImgMsg(true, "Slettet");
    } catch (e) {
      flashImgMsg(false, e.message);
    }
  };

  const handleDiagnoseUpload = (files) =>
    uploadReports(`/bikes/${bike._id}/diagnose-reports`, "diagnoseRapporter", files, setDiagnoseUploading);
  const handleDiagnoseDelete = (publicId) =>
    deleteReport(`/bikes/${bike._id}/diagnose-reports`, "diagnoseRapporter", publicId);

  const handleSoftwareUpload = (files) =>
    uploadReports(`/bikes/${bike._id}/software-reports`, "softwareRapporter", files, setSoftwareUploading);
  const handleSoftwareDelete = (publicId) =>
    deleteReport(`/bikes/${bike._id}/software-reports`, "softwareRapporter", publicId);

  if (loading) return <div className="p-6 text-gray-500">Laster sykkelkort…</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;

  const kundeName = customer
    ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() : "";
  const isReceiptPdf = bike.receiptUrl && bike.receiptUrl.includes("/raw/");

  return (
    <div className="p-4 max-w-screen-xl mx-auto">
      {lightbox && (
        <LightboxModal src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/sykkelkort")} className="text-sm text-blue-600 hover:underline">
          ← Alle sykkelkort
        </button>
        <span className="text-gray-300">|</span>
        <h1 className="text-xl font-bold text-gray-800">
          {bike.merke} {bike.modell}
          {bike.shortId && (
            <span className="ml-2 font-mono text-sm text-gray-400 font-normal">#{bike.shortId}</span>
          )}
        </h1>
        {imgMsg && (
          <span className={`ml-auto text-sm ${imgMsg.ok ? "text-green-600" : "text-red-600"}`}>
            {imgMsg.text}
          </span>
        )}
      </div>

      {/* Rad 1: Sykkelinfo + Kunde */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2 border rounded-lg p-4 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">Sykkelinformasjon</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-amber-50 rounded-lg p-2">
              <label className={labelCls}>Merke <span className="text-red-500">*</span></label>
              <input className={inputCls} value={form.merke} onChange={handleChange("merke")} placeholder="f.eks. Trek, Specialized, Scott…" />
            </div>
            <div className="bg-amber-50 rounded-lg p-2">
              <label className={labelCls}>Modell <span className="text-red-500">*</span></label>
              <input className={inputCls} value={form.modell} onChange={handleChange("modell")} placeholder="f.eks. Marlin 7, Stumpjumper…" />
            </div>
            <div className="bg-amber-50 rounded-lg p-2">
              <label className={labelCls}>Type <span className="text-red-500">*</span></label>
              <select className={inputCls + " bg-white"} value={form.type} onChange={handleChange("type")}>
                <option value="">— velg type —</option>
                {BIKE_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div className="bg-amber-50 rounded-lg p-2">
              <label className={labelCls}>Elsykkel <span className="text-red-500">*</span></label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, elsykkel: true }))}
                  className={`flex-1 py-2 rounded border text-sm font-medium transition-colors ${
                    form.elsykkel === true
                      ? "bg-green-600 border-green-600 text-white"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  }`}
                >
                  Ja
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, elsykkel: false }))}
                  className={`flex-1 py-2 rounded border text-sm font-medium transition-colors ${
                    form.elsykkel === false
                      ? "bg-gray-800 border-gray-800 text-white"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  }`}
                >
                  Nei
                </button>
              </div>
            </div>
            <div>
              <label className={labelCls}>Årsmodell</label>
              <input className={inputCls} type="number" value={form.årsmodell}
                onChange={handleChange("årsmodell")} placeholder="f.eks. 2022" min="1900" max="2100" />
            </div>
            <div>
              <label className={labelCls}>Rammenummer</label>
              <input className={inputCls} value={form.rammenummer} onChange={handleChange("rammenummer")} placeholder="Rammenummer" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">
              {saving ? "Lagrer…" : "Lagre endringer"}
            </button>
            {saveMsg && (
              <span className={`text-sm ${saveMsg.ok ? "text-green-600" : "text-red-600"}`}>{saveMsg.text}</span>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">Kundeinformasjon</h2>
          {customer ? (
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-xs text-gray-500 block">Navn</span>
                <span className="font-medium">{kundeName || "—"}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Telefon</span>
                <span>{customer.phoneNumber || customer.normalizedPhone || bike.phoneKey || "—"}</span>
              </div>
              {customer._id && (
                <button onClick={() => navigate(`/customer/${customer._id}`)}
                  className="mt-2 text-blue-600 text-xs hover:underline">
                  Åpne kundekort →
                </button>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              {bike.phoneKey ? `Telefon: ${bike.phoneKey}` : "Ingen kunde koblet"}
            </p>
          )}
        </div>
      </div>

      {/* Rad 2: Bilder */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">

        {/* Spesifikasjonsbilde */}
        <div className="border rounded-lg p-3 bg-white">
          <h2 className="font-semibold text-gray-800 mb-2 text-sm">Spesifikasjonsbilde</h2>
          {bike.specImageUrl ? (
            <div>
              <img
                src={bike.specImageUrl}
                alt="Spesifikasjoner"
                className="w-full h-32 object-contain rounded border cursor-pointer hover:opacity-90 mb-2"
                onClick={() => setLightbox({ src: bike.specImageUrl, alt: "Spesifikasjoner" })}
              />
              <div className="flex gap-1 flex-wrap">
                <UploadZone label="Bytt" accept="image/*" onFiles={handleSpecUpload} uploading={specUploading} />
                <button onClick={handleSpecDelete} className={btnDanger}>Slett</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="w-full h-32 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs mb-2">
                Ingen bilde
              </div>
              <UploadZone label="Last opp" accept="image/*" onFiles={handleSpecUpload} uploading={specUploading} />
            </div>
          )}
        </div>

        {/* Hovedbilde */}
        <div className="border rounded-lg p-3 bg-white">
          <h2 className="font-semibold text-gray-800 mb-2 text-sm">Hovedbilde</h2>
          {bike.mainImageUrl ? (
            <div>
              <img
                src={bike.mainImageUrl}
                alt="Hovedbilde"
                className="w-full h-32 object-contain rounded border cursor-pointer hover:opacity-90 mb-2"
                onClick={() => setLightbox({ src: bike.mainImageUrl, alt: "Hovedbilde" })}
              />
              <div className="flex gap-1 flex-wrap">
                <UploadZone label="Bytt" accept="image/*" onFiles={handleMainUpload} uploading={mainUploading} />
                <button onClick={handleMainDelete} className={btnDanger}>Slett</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="w-full h-32 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs mb-2">
                Ingen bilde
              </div>
              <UploadZone label="Last opp" accept="image/*" onFiles={handleMainUpload} uploading={mainUploading} />
            </div>
          )}
        </div>

        {/* Kvittering */}
        <div className="border rounded-lg p-3 bg-white">
          <h2 className="font-semibold text-gray-800 mb-2 text-sm">Kjøpskvittering</h2>
          {bike.receiptUrl ? (
            <div>
              {isReceiptPdf ? (
                <div className="w-full h-32 rounded border flex flex-col items-center justify-center text-gray-500 mb-2 gap-1">
                  <span className="text-3xl">📄</span>
                  <span className="text-xs">PDF</span>
                </div>
              ) : (
                <img
                  src={bike.receiptUrl}
                  alt="Kvittering"
                  className="w-full h-32 object-contain rounded border cursor-pointer hover:opacity-90 mb-2"
                  onClick={() => setLightbox({ src: bike.receiptUrl, alt: "Kvittering" })}
                />
              )}
              <div className="flex gap-1 flex-wrap">
                <a href={bike.receiptUrl} target="_blank" rel="noreferrer"
                  className={btnSecondary + " inline-block"}>
                  Åpne
                </a>
                <UploadZone label="Bytt" accept="image/*,application/pdf"
                  onFiles={handleReceiptUpload} uploading={receiptUploading} />
                <button onClick={handleReceiptDelete} className={btnDanger}>Slett</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="w-full h-32 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs mb-2">
                Ingen kvittering
              </div>
              <UploadZone label="Last opp" accept="image/*,application/pdf"
                onFiles={handleReceiptUpload} uploading={receiptUploading} />
              <p className="text-xs text-gray-400 mt-1">Bilde eller PDF</p>
            </div>
          )}
        </div>

        {/* Andre bilder */}
        <div className="border rounded-lg p-3 bg-white">
          <h2 className="font-semibold text-gray-800 mb-2 text-sm">Andre bilder</h2>

          {bike.extraImages?.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {bike.extraImages.map((img) => (
                <div key={img.publicId} className="relative group">
                  <img
                    src={img.url}
                    alt={img.caption || "Bilde"}
                    className="w-full h-16 object-cover rounded border cursor-pointer hover:opacity-90"
                    onClick={() => setLightbox({ src: img.url, alt: img.caption || "Bilde" })}
                  />
                  <button
                    onClick={() => handleExtraDelete(img.publicId)}
                    className="absolute top-0.5 right-0.5 bg-white/90 hover:bg-red-50 text-red-600 rounded-full w-4 h-4 text-xs flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Slett"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <UploadZone label="Last opp" accept="image/*" multiple
            onFiles={handleExtraUpload} uploading={extraUploading} />
          <p className="text-xs text-gray-400 mt-1">Komponenter, rammenr., skader o.l.</p>
        </div>
      </div>

      {/* Rad 3: Servicerapporter */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <ReportCard
          title="Diagnoserapporter"
          hint="PDF fra diagnoseverktøy"
          reports={bike.diagnoseRapporter}
          uploading={diagnoseUploading}
          onUpload={handleDiagnoseUpload}
          onDelete={handleDiagnoseDelete}
        />
        <ReportCard
          title="Softwareoppdateringsrapporter"
          hint="PDF fra softwareoppdatering"
          reports={bike.softwareRapporter}
          uploading={softwareUploading}
          onUpload={handleSoftwareUpload}
          onDelete={handleSoftwareDelete}
        />
      </div>

      {/* Servicehistorikk */}
      <div>
        <h2 className="font-semibold text-gray-800 mb-3">
          Servicehistorikk{" "}
          <span className="text-sm text-gray-400 font-normal">({history.length})</span>
        </h2>
        {history.length === 0 ? (
          <div className="border rounded-lg p-6 text-center text-gray-400 text-sm bg-white">
            Ingen servicehistorikk registrert
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100 text-sm text-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left">Ref</th>
                  <th className="px-4 py-2 text-left hidden md:table-cell">Dato</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left hidden md:table-cell">Beskrivelse</th>
                  <th className="px-4 py-2 text-right hidden md:table-cell">Beløp</th>
                </tr>
              </thead>
              <tbody>
                {history.map((svc) => {
                  const total = serviceTotal(svc);
                  const desc = serviceDesc(svc);
                  const statusLabel = STATUS_LABELS[svc.status] || svc.status || "";
                  const statusColor = STATUS_COLORS[svc.status] || "bg-gray-100 text-gray-600";
                  return (
                    <tr key={svc._id} className="border-t hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/workshop/${svc._id}`)}>
                      <td className="px-4 py-2 font-mono text-xs text-gray-600">
                        {svc.serviceRef ? `#${svc.serviceRef}` : "—"}
                      </td>
                      <td className="px-4 py-2 hidden md:table-cell text-sm text-gray-600">
                        {fmtDate(svc.createdAt)}
                      </td>
                      <td className="px-4 py-2">
                        {statusLabel && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                            {statusLabel}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 hidden md:table-cell text-sm text-gray-600 max-w-xs truncate">
                        {desc}
                      </td>
                      <td className="px-4 py-2 text-right hidden md:table-cell text-sm text-gray-700">
                        {total > 0
                          ? new Intl.NumberFormat("no-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(total)
                          : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
